import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Layers,
  RefreshCw,
  ArrowLeft,
  AlertCircle,
  TreePine,
  ChevronRight,
} from 'lucide-react';
import Header from '../../../components/header/Header';
import Footer from '../../../components/footer/Footer';
import HeroCarousel from '../../../components/carousel/HeroCarousel';
import Breadcrumb from '../../../shared/components/Breadcrumb';
import KVSLoader from '../../../components/loaders/KVSLoader';
import ActivityTree from '../components/ActivityTree';
import { useGetActivityTreeQuery } from '../services/api';
import type { ActivityTreeNode } from '../types';

import img1 from '../../../assets/pmshribanner.webp';

const ActivityTreeScreen: React.FC = () => {
  const navigate = useNavigate();
  const [selectedNode, setSelectedNode] = useState<ActivityTreeNode | null>(null);

  // Carousel images
  const carouselImages = [img1];

  // Fetch activity tree data using RTK Query
  const { data: response, isLoading, error, refetch } = useGetActivityTreeQuery();
  
  const treeData = response?.data || [];

  // Handle node click
  const handleNodeClick = useCallback((node: ActivityTreeNode) => {
    setSelectedNode(node);
  }, []);

  // Handle navigation
  const handleNavigate = useCallback(
    (path: string, node: ActivityTreeNode) => {
      // Navigate based on node type
      switch (node.type) {
        case 'head':
          // Navigate to head analytics
          navigate(`/pm-shri/analytics/heads/H${node.id}`);
          break;
        case 'subHead':
          // Extract parent head from path
          const pathParts = path.split('/').filter(Boolean);
          if (pathParts.length >= 1) {
            navigate(`/pm-shri/analytics/heads/H${pathParts[0]}/subheads/${node.id}`);
          }
          break;
        case 'activity':
          // Navigate to activity analytics
          const activityPathParts = path.split('/').filter(Boolean);
          if (activityPathParts.length >= 2) {
            navigate(
              `/pm-shri/analytics/heads/H${activityPathParts[0]}/subheads/${activityPathParts[1]}/activities/${node.id}`
            );
          }
          break;
        case 'subActivity':
          // Show details or navigate to specific sub-activity page
          // For now, just show in the details panel
          setSelectedNode(node);
          break;
        default:
          break;
      }
    },
    [navigate]
  );

  // Parse meta for display
  const parseMetaInfo = (meta: string | null): Record<string, number[]> => {
    if (!meta) return {};
    try {
      return JSON.parse(meta);
    } catch {
      return {};
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-gray-50 via-white to-blue-50">
      <Header />
      <HeroCarousel images={carouselImages} />
      <Breadcrumb />

      <main className="flex-grow container mx-auto px-4 py-6">
        {/* Page Header */}
        <div className="mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate('/pm-shri')}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ArrowLeft className="h-5 w-5 text-gray-600" />
              </button>
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 flex items-center gap-2">
                  <TreePine className="h-7 w-7 text-green-600" />
                  PM SHRI Activity Tree
                </h1>
                <p className="text-gray-600 mt-1">
                  Browse and navigate through the complete activity hierarchy
                </p>
              </div>
            </div>
            <button
              onClick={() => refetch()}
              disabled={isLoading}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
            >
              <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>

        {/* Error State */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-center gap-3">
            <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0" />
            <div className="flex-1">
              <p className="font-medium text-red-800">Error Loading Data</p>
              <p className="text-sm text-red-600">
                {typeof error === 'object' && 'message' in error 
                  ? (error as any).message 
                  : 'Failed to fetch activity tree. Please try again later.'}
              </p>
            </div>
            <button
              onClick={() => refetch()}
              className="px-3 py-1.5 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors text-sm font-medium"
            >
              Retry
            </button>
          </div>
        )}

        {/* Loading State */}
        {isLoading && (
          <div className="flex items-center justify-center h-96">
            <KVSLoader />
          </div>
        )}

        {/* Main Content */}
        {!isLoading && !error && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Activity Tree - 2 columns */}
            <div className="lg:col-span-2">
              <ActivityTree
                data={treeData}
                isLoading={isLoading}
                onNodeClick={handleNodeClick}
                onNavigate={handleNavigate}
              />
            </div>

            {/* Details Panel - 1 column */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-xl shadow-lg border border-gray-200 sticky top-4">
                <div className="bg-gradient-to-r from-purple-600 to-indigo-600 px-6 py-4 rounded-t-xl">
                  <h3 className="text-lg font-bold text-white flex items-center gap-2">
                    <Layers className="h-5 w-5" />
                    Activity Details
                  </h3>
                </div>

                <div className="p-6">
                  {selectedNode ? (
                    <div className="space-y-4">
                      {/* Title */}
                      <div>
                        <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                          Title
                        </label>
                        <p className="text-lg font-semibold text-gray-800 mt-1">
                          {selectedNode.title}
                        </p>
                      </div>

                      {/* Type Badge */}
                      <div>
                        <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                          Type
                        </label>
                        <div className="mt-1">
                          <span
                            className={`inline-block px-3 py-1 text-sm font-medium rounded-full ${
                              selectedNode.type === 'head'
                                ? 'bg-blue-100 text-blue-800'
                                : selectedNode.type === 'subHead'
                                ? 'bg-purple-100 text-purple-800'
                                : selectedNode.type === 'activity'
                                ? 'bg-green-100 text-green-800'
                                : 'bg-orange-100 text-orange-800'
                            }`}
                          >
                            {selectedNode.type === 'head'
                              ? 'Head'
                              : selectedNode.type === 'subHead'
                              ? 'Sub Head'
                              : selectedNode.type === 'activity'
                              ? 'Activity'
                              : 'Sub Activity'}
                          </span>
                        </div>
                      </div>

                      {/* Path */}
                      {/* {selectedNode.path && (
                        <div>
                          <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                            Path
                          </label>
                          <p className="text-sm font-mono text-gray-700 mt-1 bg-gray-100 px-3 py-2 rounded-lg">
                            {selectedNode.path}
                          </p>
                        </div>
                      )} */}

                      {/* Recurring Type */}
                      {selectedNode.recurringType !== null && (
                        <div>
                          <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                            Recurring Type
                          </label>
                          <div className="mt-1">
                            <span
                              className={`inline-block px-3 py-1 text-sm font-medium rounded-full ${
                                selectedNode.recurringType === 1
                                  ? 'bg-cyan-100 text-cyan-800'
                                  : 'bg-rose-100 text-rose-800'
                              }`}
                            >
                              {selectedNode.recurringType === 1
                                ? 'Recurring'
                                : 'Non-Recurring'}
                            </span>
                          </div>
                        </div>
                      )}

                      {/* Available Years */}
                      {selectedNode.meta && (
                        <div>
                          <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                            Available Years & Phases
                          </label>
                          <div className="mt-2 space-y-2">
                            {Object.entries(parseMetaInfo(selectedNode.meta)).map(
                              ([year, phases]) => (
                                <div
                                  key={year}
                                  className="flex items-center justify-between bg-gray-50 px-3 py-2 rounded-lg"
                                >
                                  <span className="font-medium text-gray-700">{year}</span>
                                  <div className="flex gap-1">
                                    {(phases as number[]).map((phase) => (
                                      <span
                                        key={phase}
                                        className="px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-700 rounded"
                                      >
                                        Phase {phase}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              )
                            )}
                          </div>
                        </div>
                      )}

                      {/* Children Count */}
                      {selectedNode.children && selectedNode.children.length > 0 && (
                        <div>
                          <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                            Children
                          </label>
                          <p className="text-lg font-semibold text-gray-800 mt-1">
                            {selectedNode.children.length} items
                          </p>
                        </div>
                      )}

                      {/* Status */}
                      <div>
                        <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                          Status
                        </label>
                        <div className="mt-1">
                          <span
                            className={`inline-block px-3 py-1 text-sm font-medium rounded-full ${
                              selectedNode.status === 1
                                ? 'bg-green-100 text-green-800'
                                : 'bg-red-100 text-red-800'
                            }`}
                          >
                            {selectedNode.status === 1 ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                      </div>

                      {/* Navigate Button */}
                      {selectedNode.path && (
                        <button
                          onClick={() => handleNavigate(selectedNode.path!, selectedNode)}
                          className="w-full mt-4 flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all font-medium"
                        >
                          Navigate to Module
                          <ChevronRight className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-12 text-gray-500">
                      <Layers className="h-16 w-16 mx-auto text-gray-300 mb-4" />
                      <p className="font-medium text-gray-600">No Activity Selected</p>
                      <p className="text-sm mt-1">
                        Click on any activity in the tree to view its details
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Quick Stats */}
        {!isLoading && !error && treeData.length > 0 && (
          <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div className="bg-white rounded-xl p-4 shadow-md border border-gray-200">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Layers className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-800">{treeData.length}</p>
                  <p className="text-xs text-gray-500">Main Heads</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-md border border-gray-200">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Layers className="h-5 w-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-800">
                    {treeData.reduce((acc, h) => acc + h.children.length, 0)}
                  </p>
                  <p className="text-xs text-gray-500">Sub Heads</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-md border border-gray-200">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <Layers className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-800">
                    {treeData.reduce(
                      (acc, h) =>
                        acc + h.children.reduce((a, s) => a + s.children.length, 0),
                      0
                    )}
                  </p>
                  <p className="text-xs text-gray-500">Activities</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-md border border-gray-200">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-orange-100 rounded-lg">
                  <Layers className="h-5 w-5 text-orange-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-800">
                    {treeData.reduce(
                      (acc, h) =>
                        acc +
                        h.children.reduce(
                          (a, s) =>
                            a +
                            s.children.reduce((b, act) => b + act.children.length, 0),
                          0
                        ),
                      0
                    )}
                  </p>
                  <p className="text-xs text-gray-500">Sub Activities</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
};

export default ActivityTreeScreen;
